import React, { useRef } from "react";

import {
  Box,
  Card,
  CardActionArea,
  Divider,
  Link,
  Theme,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import useHover from "@react-hook/hover";

import { Para12Text } from "components/shared/Typography";
import { colors } from "theme";

import { SequencingKit as IKit } from "./type";

interface IlluminaKitProps {
  kit: IKit;
  onDelete: (kitId: number) => void;
}

export const IlluminaKit: React.FC<IlluminaKitProps> = ({ kit, onDelete }) => {
  const cardRef = useRef(null);
  const isHovered = useHover(cardRef);
  const classes = useStyles();

  return (
    <Card
      square
      className={`${classes.card} ${isHovered ? "hovered" : ""}`}
      ref={cardRef}
    >
      <CardActionArea className={classes.cardActionArea} component="a">
        <Box
          display="flex"
          flexDirection="column"
          gap="16px"
          padding={2}
          height={1}
          boxSizing="border-box"
          className="sequencing-kit illumina-kit"
        >
          <Box
            display="flex"
            flex={1}
            gap="16px"
            justifyContent="space-between"
          >
            <Typography variant="h6">{kit.name}</Typography>
          </Box>
          <Divider />
          <Typography variant="h5" component="h6">
            {kit.estimatedSamplesPerRun}
            <br />
            <Para12Text className={classes.grey1}>
              Estimated Samples per Run+
            </Para12Text>
          </Typography>
          <Para12Text className={classes.grey1}>
            Calculated using {kit.correctedReadDepthFormatted} Corrected Total
            Reads per run<sup>*</sup> (2x151bp)
          </Para12Text>
        </Box>
      </CardActionArea>
      <Link
        color="secondary"
        component="button"
        type="button"
        className={`${classes.deleteBtn} hover-button`}
        onClick={() => onDelete(kit.id)}
      >
        <CloseIcon />
      </Link>
    </Card>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    height: "100%",
    position: "relative",
    background: theme.palette.grey[700],
    borderRadius: 4,
    "&.hovered": {
      background: theme.palette.secondary.light,
      "& .hover-button": {
        opacity: 1,
      },
    },
  },
  cardActionArea: {
    height: "100%",
    "& .MuiCardActionArea-focusHighlight": {
      opacity: "0 !important",
    },
    "&.Mui-focusVisible + button, &:focus-within + button": {
      opacity: 1,
    },
  },
  deleteBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    opacity: 0,
    display: "flex",
    alignSelf: "flex-start",
    color: colors.grey[300],
    "&:hover": {
      color: theme.palette.secondary.main,
    },
    "&.Mui-focusVisible": {
      opacity: 1,
    },
    "& svg": {
      width: 18,
      height: 18,
    },
  },
  grey1: {
    color: colors.grey[100],
  },
}));
